/**
 * Knowledge Hub - review changelog + status strip prototype
 *
 * Access: http://flownotes.localhost/?test=knowledge-hub-changelog
 *
 * Spec: docs/product/bets/2026-06-04-knowledge-hub-extension.md
 */

import { useMemo, useRef, useState, useCallback } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  ArrowLeft,
  AtSign,
  Bell,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Clock,
  GitCompare,
  Info,
  List,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Send,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { DiffPreviewDialog } from '@/components/layout/diff-preview-dialog';

type ReviewStatus = 'active' | 'review-requested' | 'needs-review' | 'draft';

type ChangelogAction =
  | 'verified'
  | 'review-requested'
  | 'ai-updated'
  | 'imported-from-guru'
  | 'expired';

interface ChangelogEntry {
  id: string;
  at: string;
  by: string;
  action: ChangelogAction;
  note?: string;
}

// Current body (what the reviewer sees now)
const MOCK_DOC_BODY = `# Git Merge Request Workflow

## Overview

Use this SOP when opening or reviewing merge requests for Hop client projects.

## Steps

1. Create branch from \`main\` using \`feature/<ticket>-short-slug\`.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.
4. Squash commits before merging to \`main\` unless history is meaningful.

## Notes

- Never force-push to shared branches.
- Deploybot handles staging after merge to \`develop\`.
- Confirm deploy window with client before merging client-facing changes.
`;

// Simulates document_edits.content_before from the last verify checkpoint
const MOCK_DOC_BODY_AT_LAST_VERIFY = `# Git Merge Request Workflow

## Overview

Use this SOP when opening or reviewing merge requests for Hop client projects.

## Steps

1. Create branch from \`main\` using \`feature/<ticket>-short-slug\`.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

## Notes

- Never force-push to shared branches.
- Deploybot handles staging after merge to \`develop\`.
`;

// ---------------------------------------------------------------------------
// Mock version snapshots keyed by entry ID
// Each entry shows: what the doc looked like AT that point vs the one before.
// In production these come from document_edits rows (content_before/content_after).
// ---------------------------------------------------------------------------

interface VersionSnapshot {
  before: string; // content before this event
  after: string;  // content after this event
  hasDiff: boolean;
}

const MOCK_VERSIONS: Record<string, VersionSnapshot> = {
  e4: {
    // Guru import - original sparse content
    hasDiff: false,
    before: '',
    after: `# Git Merge Request Workflow

Use this SOP when opening or reviewing merge requests for Hop client projects.

1. Create branch from main using feature/<ticket>-short-slug.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

Never force-push to shared branches.
`,
  },
  e3: {
    // Review requested - no content change, just status
    hasDiff: false,
    before: `# Git Merge Request Workflow

Use this SOP when opening or reviewing merge requests for Hop client projects.

1. Create branch from main using feature/<ticket>-short-slug.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

Never force-push to shared branches.
`,
    after: `# Git Merge Request Workflow

Use this SOP when opening or reviewing merge requests for Hop client projects.

1. Create branch from main using feature/<ticket>-short-slug.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

Never force-push to shared branches.
`,
  },
  e2: {
    // AI updated - added deploy window note + restructured to markdown headers
    hasDiff: true,
    before: `# Git Merge Request Workflow

Use this SOP when opening or reviewing merge requests for Hop client projects.

1. Create branch from main using feature/<ticket>-short-slug.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

Never force-push to shared branches.
`,
    after: `# Git Merge Request Workflow

## Overview

Use this SOP when opening or reviewing merge requests for Hop client projects.

## Steps

1. Create branch from \`main\` using \`feature/<ticket>-short-slug\`.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

## Notes

- Never force-push to shared branches.
- Deploybot handles staging after merge to \`develop\`.
- Confirm deploy window with client before merging client-facing changes.
`,
  },
  e1: {
    // Verified - step 4 (squash commits) added by Travis during review
    hasDiff: true,
    before: `# Git Merge Request Workflow

## Overview

Use this SOP when opening or reviewing merge requests for Hop client projects.

## Steps

1. Create branch from \`main\` using \`feature/<ticket>-short-slug\`.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.

## Notes

- Never force-push to shared branches.
- Deploybot handles staging after merge to \`develop\`.
- Confirm deploy window with client before merging client-facing changes.
`,
    after: `# Git Merge Request Workflow

## Overview

Use this SOP when opening or reviewing merge requests for Hop client projects.

## Steps

1. Create branch from \`main\` using \`feature/<ticket>-short-slug\`.
2. Open MR with linked Basecamp todo in description.
3. Request review from a second developer before merge.
4. Squash commits before merging to \`main\` unless history is meaningful.

## Notes

- Never force-push to shared branches.
- Deploybot handles staging after merge to \`develop\`.
- Confirm deploy window with client before merging client-facing changes.
`,
  },
};

const INITIAL_ENTRIES: ChangelogEntry[] = [
  {
    id: 'e1',
    at: '2026-04-30T18:00:00Z',
    by: 'travis@hopstudios.ca',
    action: 'verified',
    note: 'Annual re-verification after EE 7 update',
  },
  {
    id: 'e2',
    at: '2026-04-28T14:20:00Z',
    by: 'flow:basecamp-comment-ai-triage',
    action: 'ai-updated',
    note: 'Client replied: confirmed deploy window is Tuesday',
  },
  {
    id: 'e3',
    at: '2026-03-15T10:00:00Z',
    by: 'rowan@hopstudios.ca',
    action: 'review-requested',
  },
  {
    id: 'e4',
    at: '2026-03-01T12:00:00Z',
    by: 'import@guru',
    action: 'imported-from-guru',
    note: 'Guru card 8f2a91c3',
  },
];

const ACTION_LABELS: Record<ChangelogAction, string> = {
  verified: 'Verified',
  'review-requested': 'Review requested',
  'ai-updated': 'Updated by AI',
  'imported-from-guru': 'Imported from Guru',
  expired: 'Review expired',
};

const ACTION_COLORS: Record<ChangelogAction, string> = {
  verified: 'bg-green-500/10 text-green-500 border-green-500/20',
  'review-requested': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  'ai-updated': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  'imported-from-guru': 'bg-muted text-muted-foreground border-border',
  expired: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
};

// ---------------------------------------------------------------------------
// Inline version diff renderer (Google Docs style - no dialog chrome)
// ---------------------------------------------------------------------------

function computeLineDiff(before: string, after: string) {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const result: Array<{ type: 'same' | 'added' | 'removed'; text: string }> = [];

  // Simple LCS-based line diff using dynamic programming
  const m = beforeLines.length;
  const n = afterLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (beforeLines[i] === afterLines[j]) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  let i = 0, j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && beforeLines[i] === afterLines[j]) {
      result.push({ type: 'same', text: beforeLines[i] });
      i++; j++;
    } else if (j < n && (i >= m || dp[i + 1][j] <= dp[i][j + 1])) {
      result.push({ type: 'added', text: afterLines[j] });
      j++;
    } else {
      result.push({ type: 'removed', text: beforeLines[i] });
      i++;
    }
  }
  return result;
}

function InlineVersionDiff({ before, after }: { before: string; after: string }) {
  const lines = computeLineDiff(before, after);
  return (
    <div className="font-mono text-sm leading-6 px-8 py-6 max-w-3xl">
      {lines.map((line, idx) => (
        <div
          key={idx}
          className={
            line.type === 'added'
              ? 'bg-green-500/10 text-green-900 dark:text-green-200 border-l-2 border-green-500 pl-3 -mx-3'
              : line.type === 'removed'
              ? 'bg-red-500/10 text-red-900 dark:text-red-200 border-l-2 border-red-500 pl-3 -mx-3 line-through opacity-70'
              : 'pl-3 -mx-3 text-foreground'
          }
        >
          <span className="select-none mr-3 text-xs text-muted-foreground/50 w-4 inline-block">
            {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
          </span>
          {line.text || '\u00A0'}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mock workspace members (future: pulled from workspace_memberships table)
// ---------------------------------------------------------------------------

interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
  deliveryChannel: 'slack' | 'email' | 'inbox';
}

const MOCK_MEMBERS: WorkspaceMember[] = [
  { id: 'u1', name: 'Travis B.', email: 'travis@hopstudios.ca', avatar: 'TB', deliveryChannel: 'slack' },
  { id: 'u2', name: 'Rowan H.', email: 'rowan@hopstudios.ca', avatar: 'RH', deliveryChannel: 'slack' },
  { id: 'u3', name: 'Susie K.', email: 'susie@hopstudios.ca', avatar: 'SK', deliveryChannel: 'email' },
  { id: 'u4', name: 'Chris M.', email: 'chris@hopstudios.ca', avatar: 'CM', deliveryChannel: 'inbox' },
];

const DELIVERY_LABELS: Record<WorkspaceMember['deliveryChannel'], string> = {
  slack: 'Slack DM',
  email: 'Email',
  inbox: 'FlowNotes inbox',
};

const DELIVERY_COLORS: Record<WorkspaceMember['deliveryChannel'], string> = {
  slack: 'text-purple-500',
  email: 'text-blue-500',
  inbox: 'text-green-500',
};

// ---------------------------------------------------------------------------
// Request Review Modal (UI plan for Multi-User Comm + Knowledge Hub)
// ---------------------------------------------------------------------------

function RequestReviewModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (assignee: WorkspaceMember, note: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<WorkspaceMember | null>(null);
  const [note, setNote] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = MOCK_MEMBERS.filter(
    (m) =>
      m.name.toLowerCase().includes(query.toLowerCase()) ||
      m.email.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (member: WorkspaceMember) => {
    setSelected(member);
    setQuery(member.name);
    setShowDropdown(false);
  };

  const handleSubmit = () => {
    if (!selected) return;
    onSubmit(selected, note);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        data-testid="knowledge-hub-request-review-modal"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Request review</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tag a team member to verify this document
            </p>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          {/* Reviewer picker */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Reviewer
            </label>
            <div className="relative">
              <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-background focus-within:ring-1 focus-within:ring-ring">
                <AtSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <input
                  ref={inputRef}
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                  placeholder="Type a name..."
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                />
                {selected && (
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => { setSelected(null); setQuery(''); inputRef.current?.focus(); }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                {!selected && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              </div>

              {showDropdown && filtered.length > 0 && (
                <div className="absolute z-10 top-full mt-1 w-full bg-popover border border-border rounded-md shadow-md overflow-hidden">
                  {filtered.map((member) => (
                    <button
                      key={member.id}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent text-left transition-colors"
                      onMouseDown={() => handleSelect(member)}
                    >
                      <span className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground shrink-0">
                        {member.avatar}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{member.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                      </div>
                      <span className={`ml-auto text-[10px] shrink-0 ${DELIVERY_COLORS[member.deliveryChannel]}`}>
                        {DELIVERY_LABELS[member.deliveryChannel]}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Delivery channel preview */}
            {selected && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
                <Bell className="h-3 w-3" />
                <span>
                  {selected.name} will be notified via{' '}
                  <span className={`font-medium ${DELIVERY_COLORS[selected.deliveryChannel]}`}>
                    {DELIVERY_LABELS[selected.deliveryChannel]}
                  </span>
                  {selected.deliveryChannel === 'inbox' && (
                    <span className="text-muted-foreground/60"> (no Slack configured)</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Note field */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Note <span className="font-normal normal-case">(optional)</span>
            </label>
            <textarea
              className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-ring resize-none placeholder:text-muted-foreground"
              rows={3}
              placeholder="e.g. Please check the deployment steps in section 3..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* What happens next */}
          <div className="rounded-md bg-muted/50 border border-border px-3 py-2.5 space-y-1">
            <p className="text-xs font-medium text-muted-foreground">What happens</p>
            <ul className="text-xs text-muted-foreground space-y-0.5">
              <li className="flex items-start gap-1.5">
                <span className="text-foreground mt-0.5">1.</span>
                Doc status set to <code className="text-[10px] bg-muted rounded px-1">review-requested</code>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-foreground mt-0.5">2.</span>
                <span>
                  {selected
                    ? `${selected.name} receives a ${DELIVERY_LABELS[selected.deliveryChannel]} notification`
                    : 'Reviewer receives a notification'}
                  {' '}with a link to this document
                </span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-foreground mt-0.5">3.</span>
                Entry added to the review changelog
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!selected} onClick={handleSubmit}>
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Send review request
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast: confirmation after sending request
// ---------------------------------------------------------------------------

function ReviewRequestToast({
  member,
  onDismiss,
}: {
  member: WorkspaceMember;
  onDismiss: () => void;
}) {
  return (
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 bg-card border border-border rounded-lg shadow-lg px-4 py-3 max-w-xs">
      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium">Review requested</p>
        <p className="text-xs text-muted-foreground truncate">
          {member.name} notified via {DELIVERY_LABELS[member.deliveryChannel]}
        </p>
      </div>
      <button className="text-muted-foreground hover:text-foreground shrink-0" onClick={onDismiss}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ReviewChangelogPanel({
  entries,
  onClose,
  onAppendNote,
  onViewDiff,
  selectedEntryId,
  onSelectEntry,
}: {
  entries: ChangelogEntry[];
  onClose: () => void;
  onAppendNote: () => void;
  onViewDiff: () => void;
  selectedEntryId: string | null;
  onSelectEntry: (id: string | null) => void;
}) {
  return (
    <div
      className="flex flex-col h-full bg-muted/30"
      data-secondary-panel
      data-panel-type="review-changelog"
      data-testid="knowledge-hub-changelog-panel"
    >
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Review changelog</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            From <code className="text-[10px]">yaml review-changelog</code> fence
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onAppendNote}>
            <Plus className="h-3 w-3 mr-1" />
            Append
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {selectedEntryId && (
          <button
            className="w-full text-left text-xs text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1"
            onClick={() => onSelectEntry(null)}
          >
            <X className="h-3 w-3" /> Back to current
          </button>
        )}
        <div className="relative pl-4 border-l border-border space-y-2">
          {entries.map((entry, index) => {
            const version = MOCK_VERSIONS[entry.id];
            const isSelected = selectedEntryId === entry.id;
            const isClickable = !!version;
            return (
              <div
                key={entry.id}
                className={`relative rounded-md transition-colors ${
                  isClickable ? 'cursor-pointer' : ''
                } ${
                  isSelected
                    ? 'bg-accent ring-1 ring-ring -mx-2 px-2 py-2'
                    : isClickable
                    ? 'hover:bg-accent/50 -mx-2 px-2 py-2'
                    : 'py-2'
                }`}
                onClick={() => isClickable && onSelectEntry(isSelected ? null : entry.id)}
              >
                <span
                  className={`absolute -left-[21px] top-3.5 h-2.5 w-2.5 rounded-full border-2 border-background ${
                    isSelected
                      ? 'bg-primary'
                      : index === 0
                      ? 'bg-green-500'
                      : 'bg-muted-foreground/40'
                  }`}
                />
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`border text-[10px] font-medium ${ACTION_COLORS[entry.action]}`}>
                      {ACTION_LABELS[entry.action]}
                    </Badge>
                    {version?.hasDiff && (
                      <span className="text-[10px] text-muted-foreground">
                        {isSelected ? 'viewing diff' : 'has changes'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {entry.by} &middot; {formatDate(entry.at)}
                  </p>
                  {entry.note && (
                    <p className="text-xs text-foreground leading-snug">{entry.note}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-3 border-t border-border bg-card space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Since last verify
        </p>
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-2 text-xs">
            <GitCompare className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-green-500">+18 lines</span>
            <span className="text-red-500">-3 lines</span>
            <span className="text-muted-foreground">Apr 30 to now</span>
          </div>
          <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={onViewDiff}>
            View diff
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusStrip({
  status,
  verifiedBy,
  verifiedAt,
  dueLabel,
  onRequestReview,
  onMarkVerified,
}: {
  status: ReviewStatus;
  verifiedBy?: string;
  verifiedAt?: string;
  dueLabel?: string;
  onRequestReview: () => void;
  onMarkVerified: () => void;
}) {
  if (status === 'active') {
    return (
      <div
        className="flex items-center justify-between gap-3 px-6 py-2 border-b border-border bg-green-500/5 text-sm"
        data-testid="knowledge-hub-status-strip"
      >
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
          <span className="truncate">
            Verified &middot; {verifiedBy} &middot; {verifiedAt && formatDate(verifiedAt)}
            {dueLabel && (
              <span className="text-muted-foreground"> &middot; Next review {dueLabel}</span>
            )}
          </span>
        </div>
        <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={onRequestReview}>
          Request review
        </Button>
      </div>
    );
  }

  if (status === 'review-requested') {
    return (
      <div
        className="flex items-center justify-between gap-3 px-6 py-2 border-b border-border bg-blue-500/5 text-sm"
        data-testid="knowledge-hub-status-strip"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-blue-500 shrink-0" />
          <span className="truncate">Review requested &middot; awaiting verifier</span>
        </div>
        <Button size="sm" className="h-7 shrink-0" onClick={onMarkVerified}>
          Mark verified
        </Button>
      </div>
    );
  }

  if (status === 'needs-review') {
    return (
      <div
        className="flex items-center justify-between gap-3 px-6 py-2 border-b border-border bg-orange-500/5 text-sm"
        data-testid="knowledge-hub-status-strip"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-orange-500 shrink-0" />
          <span className="truncate">
            Needs review &middot; review interval passed
            {dueLabel && (
              <span className="text-orange-600 dark:text-orange-500"> &middot; overdue {dueLabel}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" className="h-7" onClick={onRequestReview}>
            Request review
          </Button>
          <Button size="sm" className="h-7" onClick={onMarkVerified}>
            Mark verified
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-between gap-3 px-6 py-2 border-b border-border bg-yellow-500/5 text-sm"
      data-testid="knowledge-hub-status-strip"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="h-4 w-4 text-yellow-500 shrink-0" />
        <span className="truncate">
          Draft &middot; not verified
          {dueLabel && (
            <span className="text-yellow-600 dark:text-yellow-500"> &middot; overdue {dueLabel}</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" className="h-7" onClick={onRequestReview}>
          Request review
        </Button>
        <Button size="sm" className="h-7" onClick={onMarkVerified}>
          Mark verified
        </Button>
      </div>
    </div>
  );
}

export function KnowledgeHubChangelogTest() {
  const [status, setStatus] = useState<ReviewStatus>('active');
  const [panelOpen, setPanelOpen] = useState(true);
  const [entries, setEntries] = useState<ChangelogEntry[]>(INITIAL_ENTRIES);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [toast, setToast] = useState<WorkspaceMember | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const selectedEntry = selectedEntryId ? entries.find((e) => e.id === selectedEntryId) ?? null : null;
  const selectedVersion = selectedEntryId ? MOCK_VERSIONS[selectedEntryId] ?? null : null;

  const dueLabel = useMemo(() => {
    if (status === 'draft') return '14 days';
    if (status === 'needs-review') return '32 days';
    if (status === 'active') return 'Jul 2027';
    return undefined;
  }, [status]);

  const handleMarkVerified = () => {
    setStatus('active');
    setEntries((prev) => [
      {
        id: `e-${Date.now()}`,
        at: new Date().toISOString(),
        by: 'rowan@hopstudios.ca',
        action: 'verified',
        note: 'Prototype verify action',
      },
      ...prev,
    ]);
  };

  const handleRequestReview = () => {
    setShowRequestModal(true);
  };

  const handleViewDiff = useCallback(() => setShowDiff(true), []);

  const handleDiffAccept = () => {
    setShowDiff(false);
    handleMarkVerified();
  };

  const handleReviewSubmit = (member: WorkspaceMember, note: string) => {
    setStatus('review-requested');
    setEntries((prev) => [
      {
        id: `e-${Date.now()}`,
        at: new Date().toISOString(),
        by: 'rowan@hopstudios.ca',
        action: 'review-requested',
        note: note
          ? `@${member.name}: ${note}`
          : `@${member.name} requested as reviewer`,
      },
      ...prev,
    ]);
    setShowRequestModal(false);
    setToast(member);
    setTimeout(() => setToast(null), 4000);
  };

  const handleAppendNote = () => {
    setEntries((prev) => [
      {
        id: `e-${Date.now()}`,
        at: new Date().toISOString(),
        by: 'rowan@hopstudios.ca',
        action: 'review-requested',
        note: 'Manual append from prototype',
      },
      ...prev,
    ]);
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {showRequestModal && (
        <RequestReviewModal
          onClose={() => setShowRequestModal(false)}
          onSubmit={handleReviewSubmit}
        />
      )}
      <DiffPreviewDialog
        open={showDiff}
        onOpenChange={setShowDiff}
        original={MOCK_DOC_BODY_AT_LAST_VERIFY}
        proposed={MOCK_DOC_BODY}
        filename="git-merge-request-workflow.md"
        onAccept={handleDiffAccept}
        onReject={() => setShowDiff(false)}
      />
      {toast && (
        <ReviewRequestToast member={toast} onDismiss={() => setToast(null)} />
      )}
      <div className="border-b px-4 py-3 bg-sidebar shrink-0">
        <a
          href="/?test=index"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Prototype Index
        </a>
        <h1 className="text-lg font-semibold">Knowledge Hub - review changelog</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Status strip + <code>review-changelog</code> secondary panel. Toggle status below.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(['active', 'needs-review', 'review-requested', 'draft'] as ReviewStatus[]).map((s) => (
            <Button
              key={s}
              variant={status === s ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setStatus(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      <div className="border-b px-4 py-2 bg-card flex items-center gap-2 text-xs text-muted-foreground shrink-0">
        <span>Hop Studios</span>
        <span>/</span>
        <span>internal</span>
        <span>/</span>
        <span>sop</span>
        <span>/</span>
        <span className="text-foreground">git-merge-request-workflow.md</span>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <List className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Info className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={panelOpen ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => setPanelOpen((v) => !v)}
            title="Review changelog panel"
          >
            <BookOpen className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <StatusStrip
        status={status}
        verifiedBy="travis@hopstudios.ca"
        verifiedAt="2026-04-30T18:00:00Z"
        dueLabel={dueLabel}
        onRequestReview={handleRequestReview}
        onMarkVerified={handleMarkVerified}
      />

      <PanelGroup direction="horizontal" className="flex-1 min-h-0">
        <Panel defaultSize={panelOpen ? 62 : 100} minSize={40}>
          <div className="h-full flex flex-col">
            {/* Version mode banner */}
            {selectedEntry && (
              <div className="flex items-center justify-between gap-3 px-6 py-2 bg-primary/5 border-b border-primary/20 text-xs shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`inline-block h-2 w-2 rounded-full shrink-0 ${
                      ACTION_COLORS[selectedEntry.action].split(' ')[0]
                    }`}
                  />
                  <span className="font-medium">
                    {ACTION_LABELS[selectedEntry.action]}
                  </span>
                  <span className="text-muted-foreground">&middot;</span>
                  <span className="text-muted-foreground truncate">
                    {formatDate(selectedEntry.at)} &middot; {selectedEntry.by}
                  </span>
                  {selectedVersion?.hasDiff ? (
                    <span className="text-muted-foreground">&middot; showing changes at this point</span>
                  ) : (
                    <span className="text-muted-foreground">&middot; no content change</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="h-6 text-xs" disabled>
                    Restore this version
                  </Button>
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setSelectedEntryId(null)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {selectedEntry && selectedVersion?.hasDiff ? (
                // Google Docs-style: inline annotated diff for this version
                <div>
                  <div className="px-8 pt-6 pb-2">
                    <h1 className="text-2xl font-bold">Git Merge Request Workflow</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                      Showing changes introduced at this checkpoint
                    </p>
                  </div>
                  <InlineVersionDiff
                    before={selectedVersion.before}
                    after={selectedVersion.after}
                  />
                </div>
              ) : selectedEntry && !selectedVersion?.hasDiff ? (
                // No content change at this entry - show the doc as-was
                <div className="px-8 py-6 max-w-3xl">
                  <h1 className="text-2xl font-bold mb-2">Git Merge Request Workflow</h1>
                  <p className="text-xs text-muted-foreground mb-6">
                    No content changes at this checkpoint - showing document state at this time.
                  </p>
                  <MarkdownPreview content={selectedVersion?.after ?? MOCK_DOC_BODY} className="max-w-none prose-sm" />
                </div>
              ) : (
                // Normal view
                <div className="px-8 py-6 max-w-3xl">
                  <h1 className="text-2xl font-bold mb-6">Git Merge Request Workflow</h1>
                  <MarkdownPreview content={MOCK_DOC_BODY} className="max-w-none prose-sm" />
                  <div className="mt-12 pt-6 border-t border-dashed border-border">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/40 text-xs text-muted-foreground">
                      <BookOpen className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        <code className="text-[10px]">yaml review-changelog</code> fence &mdash;
                        {entries.length} {entries.length === 1 ? 'entry' : 'entries'} &mdash;
                        rendered in panel (hidden in preview mode)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Panel>

        {panelOpen && (
          <>
            <PanelResizeHandle className="w-1 bg-border hover:bg-primary/30 transition-colors" />
            <Panel defaultSize={38} minSize={28} maxSize={50}>
              <ReviewChangelogPanel
                entries={entries}
                onClose={() => setPanelOpen(false)}
                onAppendNote={handleAppendNote}
                onViewDiff={handleViewDiff}
                selectedEntryId={selectedEntryId}
                onSelectEntry={setSelectedEntryId}
              />
            </Panel>
          </>
        )}
      </PanelGroup>
    </div>
  );
}
